import { useEffect } from 'react';

/**
 * 着席中は画面の自動スリープを抑止する（要件5.5）。
 *
 * 画面が消えないため、視線を戻せば即座に自分のターンを判別できる。端末側の通知手段
 * （バイブレーション、音）は使わない。iOS Safari が Vibration API を実装しておらず、
 * 音は autoplay ポリシーの制約を受けるため、端末をまたいで一貫した通知が成立しない。
 */

type WakeLockSentinelLike = { released: boolean; release: () => Promise<void> };
type WakeLockLike = { request: (type: 'screen') => Promise<WakeLockSentinelLike> };

function wakeLock(): WakeLockLike | null {
  const candidate = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
  return candidate ?? null;
}

export function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const api = wakeLock();
    if (api === null) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async (): Promise<void> => {
      try {
        const next = await api.request('screen');
        if (cancelled) {
          await next.release();
          return;
        }
        sentinel = next;
      } catch {
        // 権限が無い、またはタブが非表示。次の可視化で再試行する。
      }
    };

    // タブが再び表示されたとき、解放されたロックを取り直す。
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible' && (sentinel === null || sentinel.released)) {
        void acquire();
      }
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => undefined);
    };
  }, [enabled]);
}
